import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import Nav from "../landing-pages/home-two/components/nav";
import { MTN_YELLOW } from "../../constants/Colors";
import { useHideEbanqoWidget } from "../../hooks/useHideEbanqoWidget";
import GameOverPanel from "../../components/games/GameOverPanel";
import HapticsToggle from "../../components/games/HapticsToggle";
import { HAPTIC, haptic, stopHaptics } from "../../lib/haptics";
import {
  endOfRunMessage,
  pickPraise,
  runWentWell,
} from "../../components/games/encouragement";
import {
  GROUP_LABELS,
  type Food,
  type FoodGroup,
} from "../../components/games/market/market-config";
import {
  LIVES,
  buildBelt,
  grabScore,
  isWanted,
  GOOD_SCORE,
  pickTaunt,
  planRound,
  readBestScore,
  roundBonus,
  writeBestScore,
  type RoundPlan,
} from "../../components/games/market/market-rules";

/**
 * Makola Market Plate. The stall rolls past; take what the round asks for and
 * leave the rest. Grab the wrong thing and it costs a life; three mistakes
 * ends the run.
 *
 * What it exercises is selective attention and inhibition — taking the right
 * thing is easy, not taking the nearest thing is the work. Plate rounds add
 * working memory on top, because you have to hold what is already on the
 * plate.
 *
 * The belt items are DOM buttons rather than meshes: a moving target has to
 * stay a real tap target with a real label, and the Ghanaian names are the
 * point of this game. The stall behind them is the three.js part, and it
 * draws once per resize because nothing in it moves.
 *
 * On the health content: rounds ask for food GROUPS and for the plate model.
 * Nothing here calls a food good or bad, and there are deliberately no
 * low-salt or diabetes rounds — that mapping is clinical guidance and needs a
 * clinician, not a developer. See market-config.
 */
const MarketStall = lazy(
  () => import("../../components/games/market/MarketStall"),
);

const TICK_MS = 60;
const ROUND_BREAK_MS = 1300;

type Phase = "idle" | "running" | "roundOver" | "over";

/** One item travelling the belt. */
interface BeltItem {
  id: number;
  food: Food;
  /** Wall clock at which it entered. */
  startedAtMs: number;
  taken: boolean;
}

export default function MakolaPage() {
  useHideEbanqoWidget("makola-plate");

  const [phase, setPhase] = useState<Phase>("idle");
  const [round, setRound] = useState(1);
  const [plan, setPlan] = useState<RoundPlan>(() => planRound(1));
  const [items, setItems] = useState<BeltItem[]>([]);
  const [collected, setCollected] = useState<FoodGroup[]>([]);
  const [lives, setLives] = useState(LIVES);
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(0);
  const [roundsCleared, setRoundsCleared] = useState(0);
  const [flash, setFlash] = useState<string | null>(null);
  const [endMessage, setEndMessage] = useState("");
  /** The score as the run ends, read by the end-of-run message before
   *  writeBestScore moves the goalposts. */
  const scoreRef = useRef(0);
  /** The best as the run began. The panel's face and the end-of-run line both
   *  need it, and writeBestScore has already moved it by then. */
  const bestBeforeRun = useRef(0);
  const [paused, setPaused] = useState(false);
  /** How much of the round's clock is left, 1 to 0. The round always had a
   *  timeout — run out with the quota unfilled and it costs a life — but with
   *  nothing on screen it arrived as a telling-off rather than as a clock
   *  (Ken, 2026-09-04). */
  const [roundRemaining, setRoundRemaining] = useState(1);

  const beltRef = useRef<Food[]>([]);
  const spawnedRef = useRef(0);
  const startedAtRef = useRef(0);
  const collectedRef = useRef<FoodGroup[]>([]);
  const livesRef = useRef(LIVES);
  const runOver = useRef(false);
  const nextId = useRef(1);
  const timer = useRef<number | null>(null);
  const timeouts = useRef<number[]>([]);
  const pausedAt = useRef<number | null>(null);

  const later = useCallback((fn: () => void, ms: number) => {
    timeouts.current.push(window.setTimeout(fn, ms));
  }, []);
  const clearTimers = useCallback(() => {
    if (timer.current !== null) window.clearInterval(timer.current);
    timeouts.current.forEach(window.clearTimeout);
    timeouts.current = [];
  }, []);

  // Mirrors score, so the end-of-run message can read the final value
  // without waiting for a re-render.
  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  useEffect(() => {
    setBest(readBestScore());
    return clearTimers;
  }, [clearTimers]);

  const say = useCallback(
    (message: string, ms = 900) => {
      setFlash(message);
      later(() => setFlash(null), ms);
    },
    [later],
  );

  const startRound = useCallback((roundNumber: number) => {
    const nextPlan = planRound(roundNumber);
    // Say it out loud when the belt tightens. The curve was already there but
    // invisible; a player who is told it is speeding up reads the next round
    // as harder rather than as the same round going badly.
    const previousCross = planRound(Math.max(1, roundNumber - 1)).crossMs;
    if (roundNumber > 1 && nextPlan.crossMs < previousCross) {
      later(
        () =>
          say(
            nextPlan.goal.kind === "plate"
              ? "Plate round — belt speeding up"
              : "Belt speeding up",
            1300,
          ),
        350,
      );
    }
    beltRef.current = buildBelt(nextPlan);
    spawnedRef.current = 0;
    startedAtRef.current = performance.now();
    setRoundRemaining(1);
    collectedRef.current = [];
    setCollected([]);
    setItems([]);
    setRound(roundNumber);
    setPlan(nextPlan);
    setPaused(false);
    pausedAt.current = null;
    setPhase("running");
  }, [later, say]);

  const startRun = useCallback(() => {
    clearTimers();
    runOver.current = false;
    livesRef.current = LIVES;
    setLives(LIVES);
    setScore(0);
    setRoundsCleared(0);
    startRound(1);
  }, [clearTimers, startRound]);

  const loseALife = useCallback(
    (reason: string) => {
      haptic(HAPTIC.fail);
      say(reason);
      const left = Math.max(0, livesRef.current - 1);
      livesRef.current = left;
      setLives(left);

      if (left === 0 && !runOver.current) {
        runOver.current = true;
        stopHaptics();
        clearTimers();
        setPhase("over");
        // Read the old best HERE, not inside the updater: writeBestScore runs
          // in the same batch, and an updater reading it afterwards would
          // compare the run against itself, so beating your own best could
          // never earn praise.
          const previousBest = readBestScore();
          bestBeforeRun.current = previousBest;
          setEndMessage((previous) =>
            endOfRunMessage(
              {
                score: scoreRef.current,
                goodScore: GOOD_SCORE,
                previousBest,
              },
              { praise: pickPraise, taunt: pickTaunt },
              previous,
            ),
          );
        setScore((final) => {
          writeBestScore(final);
          setBest(readBestScore());
          return final;
        });
      }
    },
    [clearTimers, say],
  );

  /** Pausing between items is harmless; the belt is the pressure, so pausing
   *  is refused while anything is still on it. */
  const pause = useCallback(() => {
    pausedAt.current = performance.now();
    setPaused(true);
  }, []);

  const resume = useCallback(() => {
    const held = pausedAt.current;
    if (held !== null) {
      const heldFor = performance.now() - held;
      startedAtRef.current += heldFor;
      setItems((current) =>
        current.map((item) => ({
          ...item,
          startedAtMs: item.startedAtMs + heldFor,
        })),
      );
      pausedAt.current = null;
    }
    setPaused(false);
  }, []);

  // A hidden tab would otherwise run the belt past an absent thumb.
  useEffect(() => {
    const onHide = () => {
      if (document.hidden && phase === "running" && !paused) pause();
    };
    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
  }, [pause, phase, paused]);

  // The belt: spawns items, retires them at the far end, ends the round.
  useEffect(() => {
    if (phase !== "running" || paused) return;
    const roundPlan = plan;

    timer.current = window.setInterval(() => {
      const now = performance.now();
      const elapsed = now - startedAtRef.current;
      setRoundRemaining(
        Math.max(0, Math.min(1, 1 - elapsed / roundPlan.durationMs)),
      );

      // Spawn on schedule while the belt has stock.
      const due = Math.floor(elapsed / roundPlan.spawnEveryMs) + 1;
      if (due > spawnedRef.current && spawnedRef.current < beltRef.current.length) {
        const food = beltRef.current[spawnedRef.current];
        spawnedRef.current += 1;
        setItems((current) => [
          ...current,
          { id: nextId.current++, food, startedAtMs: now, taken: false },
        ]);
      }

      // Retire anything that has crossed.
      setItems((current) =>
        current.filter((item) => now - item.startedAtMs < roundPlan.crossMs + 400),
      );

      if (elapsed >= roundPlan.durationMs) {
        window.clearInterval(timer.current as number);
        setPhase((current) => (current === "running" ? "roundOver" : current));
      }
    }, TICK_MS);

    return () => {
      if (timer.current !== null) window.clearInterval(timer.current);
    };
  }, [phase, plan, paused]);

  // Round over: pay the bonus if the quota was filled, else spend a life.
  useEffect(() => {
    if (phase !== "roundOver") return;

    const filled = collectedRef.current.length >= plan.goal.quota;
    if (filled) {
      const bonus = roundBonus(round, plan.goal.kind);
      haptic(HAPTIC.success);
      setScore((current) => current + bonus);
      setRoundsCleared((current) => Math.max(current, round));
      say(`Round done — +${bonus}`, 1200);
      later(() => startRound(round + 1), ROUND_BREAK_MS);
      return;
    }

    loseALife("Quota not filled");
    later(() => {
      if (!runOver.current) startRound(round + 1);
    }, ROUND_BREAK_MS);
  }, [phase, plan, round, say, later, startRound, loseALife]);

  const grab = (item: BeltItem) => {
    if (phase !== "running" || paused || item.taken) return;

    const wanted = isWanted(plan.goal, item.food, collectedRef.current);
    setItems((current) =>
      current.map((candidate) =>
        candidate.id === item.id ? { ...candidate, taken: true } : candidate,
      ),
    );

    if (!wanted) {
      loseALife(`Not ${item.food.label.toLowerCase()}`);
      return;
    }

    haptic(HAPTIC.tick);
    collectedRef.current = [...collectedRef.current, item.food.group];
    setCollected(collectedRef.current);
    const gained = grabScore(round, plan.crossMs);
    setScore((current) => current + gained);
    say(`+${gained}`, 700);

    // Quota filled early ends the round there — standing on a finished belt
    // is dead time.
    if (collectedRef.current.length >= plan.goal.quota) {
      if (timer.current !== null) window.clearInterval(timer.current);
      setPhase("roundOver");
    }
  };

  const statusLine = (() => {
    switch (phase) {
      case "running":
        return roundRemaining < 0.25
          ? `${plan.goal.label} — hurry!`
          : plan.goal.label;
      case "roundOver":
        return "Round over";
      case "over":
        return "Run over";
      default:
        return "Take what the round asks for. Leave the rest.";
    }
  })();

  return (
    <div className="flex min-h-screen w-full flex-col bg-neutral-950 text-white">
      <div className="text-black">
        <Nav />
      </div>

      <main
        className={`mx-auto flex w-full max-w-md flex-1 flex-col px-4 sm:max-w-xl md:max-w-2xl lg:max-w-3xl py-5 ${
          // Centre the game in whatever screen it gets: these boards are a
          // fixed share of the height, and on a tall phone the leftover used
          // to pile up under the last row. Not while the run-over panel is
          // up — that content is taller than the screen, and centring an
          // overflowing column puts its top out of reach.
          phase === "over" ? "" : "justify-center"
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="m-0 text-lg font-bold sm:text-2xl">Makola Market Plate</h1>
            <p className="m-0 text-[11px] text-white/40 sm:text-sm">Build the plate</p>
          </div>
          <div className="flex items-center gap-3 text-right">
            <div>
              <p className="m-0 text-[10px] uppercase tracking-wide text-white/40 sm:text-xs">
                Score
              </p>
              <p className="m-0 text-base font-bold tabular-nums sm:text-2xl">{score}</p>
            </div>
            <div className="flex gap-0.5" aria-label={`${lives} lives left`}>
              {Array.from({ length: LIVES }, (_, i) => (
                <Heart
                  key={i}
                  className="h-4 w-4"
                  strokeWidth={2.5}
                  style={{
                    color: i < lives ? MTN_YELLOW : "rgba(255,255,255,0.18)",
                    fill: i < lives ? MTN_YELLOW : "transparent",
                  }}
                  aria-hidden
                />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2 text-[11px] text-white/45 sm:text-sm">
          <span>
            {phase === "idle"
              ? "Ready"
              : `Round ${round} · ${collected.length}/${plan.goal.quota}`}
          </span>
          <div className="flex items-center gap-2">
            {phase === "running" && !paused && items.length === 0 && (
              <button
                type="button"
                onClick={pause}
                className="rounded-md border border-white/20 px-2 py-0.5 text-[11px] font-semibold text-white/70 sm:px-3 sm:py-1 sm:text-sm"
              >
                Pause
              </button>
            )}
            <span>Best {best}</span>
          </div>
        </div>

        {/* Stall behind, belt in front. */}
        <div
          className={`relative mt-3 w-full overflow-hidden rounded-2xl bg-neutral-900 ring-1 ring-white/10 ${
            phase === "over" ? "h-[22vh] min-h-[150px]" : "h-[40vh] min-h-[220px] md:h-[50vh]"
          }`}
        >
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center text-xs text-white/40 sm:text-base">
                Setting up the stall…
              </div>
            }
          >
            <MarketStall />
          </Suspense>

          {/* The belt. Real buttons, so a moving target is still a tap target
              with a name on it. */}
          <div className="pointer-events-none absolute inset-x-0 bottom-[24%] top-[44%]">
            {items.map((item) => {
              const age = performance.now() - item.startedAtMs;
              const travelled = Math.min(1.15, age / plan.crossMs);
              // A slow bob, phased off the item's id so the row is not a
              // marching band. Goods carried past a stall move.
              const bob = Math.sin(age / 420 + item.id) * 3;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => grab(item)}
                  disabled={item.taken}
                  aria-label={`${item.food.label} — ${GROUP_LABELS[item.food.group]}`}
                  className={[
                    "pointer-events-auto absolute flex w-[78px] flex-col items-center gap-1 rounded-xl px-1 pb-1.5 pt-2 text-[10px] font-bold sm:w-[104px] sm:gap-1.5 sm:pb-2.5 sm:pt-3 sm:text-sm",
                    item.taken
                      ? "scale-125 opacity-0"
                      : "bg-[#FFF8EA] text-neutral-900 shadow-[0_6px_14px_rgba(0,0,0,0.35)] ring-1 ring-black/10 active:bg-white",
                  ].join(" ")}
                  style={{
                    // Right to left, the way a belt runs.
                    left: `${100 - travelled * 118}%`,
                    top: "50%",
                    transform: `translate(-50%, calc(-50% + ${bob}px))`,
                    transition: "opacity 180ms linear, transform 180ms ease-out",
                  }}
                >
                  <span
                    className={[
                      "shrink-0",
                      item.food.shape === "round"
                        ? "h-7 w-7 rounded-full"
                        : item.food.shape === "oval"
                          ? "h-6 w-8 rounded-full"
                          : item.food.shape === "long"
                            ? "h-3 w-8 rounded-full"
                            : item.food.shape === "leaf"
                              ? "h-7 w-6 rounded-[60%_10%_60%_10%]"
                              : "h-6 w-8 rounded-t-full",
                    ].join(" ")}
                    style={{ backgroundColor: item.food.colour }}
                    aria-hidden
                  />
                  {item.food.label}
                  <span
                    className="absolute -bottom-1.5 h-1.5 w-10 rounded-full bg-black/30 blur-[2px]"
                    aria-hidden
                  />
                </button>
              );
            })}
          </div>

          {phase === "running" && paused && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-neutral-950/85 px-6 text-center">
              <p className="m-0 text-sm font-semibold">Paused</p>
              <button
                type="button"
                onClick={resume}
                className="rounded-lg px-5 py-2.5 text-sm font-semibold text-black"
                style={{ backgroundColor: MTN_YELLOW }}
              >
                Resume
              </button>
            </div>
          )}

          {phase === "idle" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-neutral-950/85 px-6 text-center">
              <p className="m-0 max-w-xs text-xs leading-relaxed text-white/60 sm:max-w-md sm:text-base">
                The stall rolls past. Take only what the round asks for — and
                later on, build a whole plate.
              </p>
              <button
                type="button"
                onClick={startRun}
                className="rounded-lg px-5 py-2.5 text-sm font-semibold text-black"
                style={{ backgroundColor: MTN_YELLOW }}
              >
                Start
              </button>
            </div>
          )}
        </div>

        {/* The round clock. Only while a round is running — a full bar on the
            results screen would read as time the player still had. */}
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-white/5">
          {phase === "running" && (
            <div
              className="h-full transition-[width] duration-100"
              style={{
                width: `${Math.round(roundRemaining * 100)}%`,
                backgroundColor: roundRemaining < 0.25 ? "#E83B46" : MTN_YELLOW,
              }}
            />
          )}
        </div>

        <p className="mt-2 text-center text-xs text-white/70 sm:text-lg" aria-live="polite">
          {flash ?? statusLine}
        </p>

        {/* What is already on the plate, for plate rounds. */}
        {phase === "running" && plan.goal.kind === "plate" && (
          <div className="mt-2 flex items-center justify-center gap-2">
            {plan.goal.plate?.map((group) => {
              const have = collected.includes(group);
              return (
                <span
                  key={group}
                  className={[
                    "rounded-lg px-2.5 py-1 text-[11px] font-semibold sm:px-4 sm:py-2 sm:text-base",
                    have ? "text-black" : "bg-white/10 text-white/45",
                  ].join(" ")}
                  style={have ? { backgroundColor: MTN_YELLOW } : undefined}
                >
                  {GROUP_LABELS[group]}
                </span>
              );
            })}
          </div>
        )}

        {phase === "over" && (
          <GameOverPanel
            score={score}
            best={best}
            message={endMessage}
            wentWell={runWentWell({
              score,
              goodScore: GOOD_SCORE,
              previousBest: bestBeforeRun.current,
            })}
            peak={{
              value: roundsCleared,
              singular: "round",
              plural: "rounds",
              verb: "cleared",
            }}
            gameSlug="makola-plate"
            rounds={roundsCleared}
            onPlayAgain={startRun}
          />
        )}

        <div className="mt-5 flex items-center justify-between gap-3 text-[11px] text-white/35 sm:text-sm">
          <Link to="/play" className="underline">
            All games
          </Link>
          <HapticsToggle />
          <Link to="/play/leaderboard?game=makola-plate" className="underline">
            Leaderboard
          </Link>
        </div>
      </main>
    </div>
  );
}
